import { Server } from "./Server";
import { Collection, HandlersCollection, Ref, RefString } from "@tblabs/truffle";
import { Filter } from "../Models/Filter";
import { ConnectionStatus } from "../Models/DeviceConnectionStatus";
import { RawDeviceProfile } from "../Models/RawDeviceProfile";
import { PictureForWebClient } from "../Models/PictureForWebClient";
import { DeviceConfig } from "../Models/DeviceConfig";
import { DeviceSettingsValidationResult } from "./DeviceSettingsValidationResult";
import { Device } from "./Device";
import { IoState } from "./IoState";


export class DevicesManager
{
    public Devices = new Collection<Device>();
    public GroupFilter = new Ref<Filter>(Filter.Online).Storable("group-filter");
    public IdFilter = new RefString().Storable("id-filter");
    public Status = new RefString();

    public OnProblem = new HandlersCollection<(problem: string, counter: number) => void>();
    public OnNewDevice = new HandlersCollection<(device: Device) => void>();
    public OnDeviceLost = new HandlersCollection<(tid: string) => void>();
    public OnSettingsValidationResult = new HandlersCollection<(tid: string, validationResult: DeviceSettingsValidationResult, config: DeviceConfig) => void>();

    constructor(private _server: Server)
    {
        this._server.OnDevicesUpdate.Subscribe((profiles: RawDeviceProfile[]) =>
        {
            let devices = profiles.map(raw => new Device(this._server, raw))

            if (this.IdFilter.value)
            {
                const id = this.IdFilter.value.trim().toLowerCase();
                devices = devices.filter(device => device.Id.toLowerCase().includes(id));
            }

            this.Devices.Load(...devices);

            if (devices.length == 0)
                this.Status.value = `No ${this.GroupFilter.value} devices found`;
            else
                this.Status.value = "";
        });

        this._server.OnNewDevice.Subscribe((raw: RawDeviceProfile) =>
        {
            if (this.GetDevice(raw.Id))
            {
                this.UpdateStatus(raw.Id, ConnectionStatus.Connected);
                return;
            }

            const newDevice = new Device(this._server, raw);
            this.Devices.Add(newDevice);

            this.OnNewDevice.Call(newDevice);
        });

        this._server.OnDeviceLost.Subscribe((tid: string) =>
        {
            this.UpdateStatus(tid, ConnectionStatus.Disconnected);

            this.OnDeviceLost.Call(tid);
        });

        this._server.OnPictureUpdate.Subscribe((tid: string, pic: PictureForWebClient) =>
        {
            this.UpdatePicture(tid, pic);
            this.UpdateStatus(tid, ConnectionStatus.Connected);
        });

        this._server.OnSettingsValidationResult.Subscribe((tid: string, validationResult: DeviceSettingsValidationResult, config: DeviceConfig) =>
        {
            if (this._server.DeviceSettingsUpdateRequestCache.IsPending(tid))
            {
                this._server.DeviceSettingsUpdateRequestCache.Remove(tid);

                this.OnSettingsValidationResult.Call(tid, validationResult);
            }
        });

        this._server.OnIoStateUpdate.Subscribe((tid: string, ioState: IoState) =>
        {
            const device = this.GetDevice(tid);
            if (!device) return;

            device.IoState = ioState;
            console.log(`Io state updated for ${tid}: ${JSON.stringify(ioState, undefined, 4)}`);
        });
    }

    public Init(): void
    {
        this._server.Connect(this.GroupFilter.value);
    }

    public GetDevices(): void
    {
        this.Status.value = `Fetching ${this.GroupFilter.value} devices...`;

        this._server.GetDevices(this.GroupFilter.value);
    }

    public GetDevice(id: string): Device | undefined
    {
        return this.Devices.Find(x => x.Id.toLowerCase() == id.toLowerCase());
    }

    public UpdatePicture(id: string, pic: PictureForWebClient): void
    {
        const dev: Device = this.GetDevice(id);

        if (!dev || !pic) return;

        dev.Thumbnail.object = pic;
    }

    public UpdateStatus(id: string, status: ConnectionStatus): void
    {
        const device = this.GetDevice(id);

        if (!device) return;

        device.ConnectionStatus.value = status;
    }
}
